import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement, type ReactNode } from "react";
import { ConfirmDialog, ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { QuoteConvertResult, QuotePdfLink, QuoteStatusRef, QuoteView } from "@cx/contracts";
import {
  COMMERCIAL_TERMS_LABELS,
  FREIGHT_MODE_LABELS,
  formatBRL,
  formatDateBR,
  formatDocument,
  formatPct,
  formatQty,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { convertBlockReason } from "./labels";

/** Proposta gravada: dados, itens e totais; muda situação, gera o PDF, converte em pedido e exclui. */
export function QuoteViewPage(): ReactElement {
  const { id } = useParams({ from: "/vendas/propostas/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const quote = useQuery({
    queryKey: ["quotes", companyId, "view", id],
    refetchOnMount: "always",
    queryFn: () => api<QuoteView>(`/quotes/${id}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["quotes", companyId] });
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    refresh();
  };
  const status = useMutation({
    mutationFn: (to: QuoteStatusRef) =>
      api(`/quotes/${id}/status`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ toStatusId: to.id, expectedFromStatusId: quote.data!.status.id }),
      }),
    onSuccess: (_d, to) => {
      toast("success", `Situação alterada para ${to.label}`);
      refresh();
    },
    onError,
  });
  const pdf = useMutation({
    mutationFn: () => api<QuotePdfLink>(`/quotes/${id}/pdf`),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const [convertKey] = useState(() => crypto.randomUUID());
  const convert = useMutation({
    mutationFn: () =>
      api<QuoteConvertResult>(`/quotes/${id}/convert`, {
        method: "POST",
        headers: { "Idempotency-Key": convertKey },
      }),
    onSuccess: () => {
      toast("success", "Proposta convertida em pedido de venda");
      refresh();
      qc.invalidateQueries({ queryKey: ["sales-orders", companyId] });
    },
    onError,
  });
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/quotes/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Proposta excluída");
      refresh();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
      navigate({ to: "/vendas/propostas" });
    },
    onError,
  });

  const q = quote.data;
  const canUpdate = can(perms, "quotes", "update");
  // Converter cria um pedido de venda: a rota exige também sales_orders:create.
  const canConvert = canUpdate && can(perms, "sales_orders", "create");
  const converted = !!q?.convertedOrderId;
  const blocked = q ? convertBlockReason(q) : null;
  return (
    <>
      <ContentHeader
        title={q ? `Proposta nº ${q.number}` : "Proposta"}
        breadcrumb={["Vendas", "Propostas comerciais", q ? String(q.number) : ""]}
        actions={
          q && (
            <>
              <Link to="/vendas/propostas" className="btn btn-outline-secondary">
                Voltar
              </Link>
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={pdf.isPending}
                onClick={() => pdf.mutate()}
              >
                {pdf.isPending ? "Gerando PDF…" : "Baixar PDF"}
              </button>
              {canUpdate && !converted && (
                <Link
                  to="/vendas/propostas/$id/editar"
                  params={{ id: q.id }}
                  className="btn btn-outline-primary"
                >
                  Editar
                </Link>
              )}
              {canConvert && !converted && (
                <span title={blocked ?? undefined}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!!blocked || convert.isPending}
                    aria-describedby={blocked ? "quote-convert-hint" : undefined}
                    onClick={() => setConverting(true)}
                  >
                    Converter em pedido
                  </button>
                </span>
              )}
              {can(perms, "quotes", "delete") && !converted && (
                <button type="button" className="btn btn-outline-danger" onClick={() => setDeleting(true)}>
                  Excluir
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={quote.isLoading} error={quote.error} />
      {q && (
        <>
          {blocked && canConvert && !converted && (
            <p id="quote-convert-hint" className="text-muted small">
              {blocked}
            </p>
          )}
          {converted && (
            <div className="alert alert-info" role="status">
              Convertida em pedido de venda em{" "}
              {q.convertedAt ? formatDateBR(q.convertedAt.slice(0, 10)) : "—"}. A proposta fica somente para
              consulta.
              {q.convertedOrderId && can(perms, "sales_orders", "read") && (
                <>
                  {" "}
                  <Link to="/vendas/pedidos/$id" params={{ id: q.convertedOrderId }} className="alert-link">
                    Ver pedido
                  </Link>
                </>
              )}
            </div>
          )}
          <div className="row">
            <div className="col-xl-9">
              <div className="box card p-3 mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                  <span>Situação:</span>
                  <StatusBadge tone={q.status.color}>{q.status.label}</StatusBadge>
                  {canUpdate && q.nextStatuses.length > 0 && (
                    <div
                      className="d-flex flex-wrap gap-1 ms-md-3"
                      role="group"
                      aria-label="Alterar situação"
                    >
                      {q.nextStatuses.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={status.isPending}
                          onClick={() => status.mutate(s)}
                        >
                          Marcar como {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <dl className="row mb-0">
                  <Info label="Cliente">
                    {q.contact.name}
                    {q.contact.document && (
                      <span className="text-muted ms-2">{formatDocument(q.contact.document)}</span>
                    )}
                  </Info>
                  <Info label="A/C">{q.attentionOf}</Info>
                  <Info label="Data">{formatDateBR(q.date)}</Info>
                  <Info label="Válida até">{q.validUntil && formatDateBR(q.validUntil)}</Info>
                  <Info label="Vendedor">{q.seller?.name}</Info>
                  <Info label="Lista de preços">{q.priceList?.name ?? "Preço de venda do produto"}</Info>
                  <Info label="Próximo contato">{q.nextContactAt && formatDateBR(q.nextContactAt)}</Info>
                </dl>
                {q.introduction && (
                  <p className="mt-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {q.introduction}
                  </p>
                )}
              </div>
              <div className="box card mb-3">
                <div className="table-responsive">
                  <table className="table align-middle mb-0" aria-label="Itens da proposta">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Descrição</th>
                        <th scope="col">Un</th>
                        <th scope="col" className="text-end">
                          Qtd
                        </th>
                        <th scope="col" className="text-end">
                          Preço un.
                        </th>
                        <th scope="col" className="text-end">
                          Desc.
                        </th>
                        <th scope="col" className="text-end">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.items.map((it) => (
                        <tr key={it.position}>
                          <td>{it.position}</td>
                          <td>
                            {it.description}
                            {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                          </td>
                          <td>{it.unit ?? "—"}</td>
                          <td className="text-end">{formatQty(it.qtyQ4)}</td>
                          <td className="text-end">{formatBRL(it.listPriceCents)}</td>
                          <td className="text-end">{it.discountPct4 ? formatPct(it.discountPct4) : "—"}</td>
                          <td className="text-end">{formatBRL(it.totalCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="box card p-3 mb-3">
                <h2 className="h6">Condições</h2>
                <dl className="row mb-0">
                  <Info label="Condição comercial">
                    {q.commercialTermsMode === "installments" && q.paymentTerm
                      ? q.paymentTerm.name
                      : q.commercialTermsMode === "text"
                        ? q.commercialTermsText
                        : COMMERCIAL_TERMS_LABELS[q.commercialTermsMode]}
                  </Info>
                  <Info label="Transportador">{q.carrier?.name}</Info>
                  <Info label="Frete">{FREIGHT_MODE_LABELS[q.freightMode]}</Info>
                </dl>
                {q.installments.length > 0 && (
                  <table className="table table-sm w-auto mt-2" aria-label="Parcelas">
                    <thead>
                      <tr>
                        <th scope="col">Parcela</th>
                        <th scope="col">Vencimento</th>
                        <th scope="col" className="text-end">
                          Valor
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.installments.map((p) => (
                        <tr key={p.number}>
                          <td>{p.number}</td>
                          <td>{formatDateBR(p.dueDate)}</td>
                          <td className="text-end">{formatBRL(p.amountCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {q.generalTerms && (
                  <>
                    <h3 className="h6 mt-3">Condições gerais</h3>
                    <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                      {q.generalTerms}
                    </p>
                  </>
                )}
              </div>
            </div>
            <aside className="col-xl-3">
              <div className="box card p-3 mb-3">
                <h2 className="h6">Totais</h2>
                <dl className="row mb-0 small">
                  {(
                    [
                      ["Total dos itens", q.itemsTotalCents],
                      ["Outros itens", q.otherItemsTotalCents],
                      [
                        q.discountPct4 !== null ? `Desconto (${formatPct(q.discountPct4)})` : "Desconto",
                        -q.discountCents,
                      ],
                      ["Frete", q.freightCents],
                      ["Outras despesas", q.otherExpensesCents],
                    ] as const
                  ).map(([label, v]) => (
                    <Fragment key={label}>
                      <dt className="col-7 fw-normal">{label}</dt>
                      <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
                    </Fragment>
                  ))}
                  <dt className="col-7">Total da proposta</dt>
                  <dd className="col-5 text-end fw-bold mb-0" data-testid="quote-total">
                    {formatBRL(q.totalCents)}
                  </dd>
                </dl>
              </div>
            </aside>
          </div>
        </>
      )}
      <ConfirmDialog
        open={deleting}
        title="Excluir proposta"
        message={`Excluir a proposta nº ${q?.number ?? ""}? Ela pode ser restaurada pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          del.mutate();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
      <ConfirmDialog
        open={converting}
        title="Converter em pedido"
        message={`Gerar um pedido de venda com os itens e valores da proposta nº ${q?.number ?? ""}? A proposta passa para "Convertida" e não poderá mais ser alterada.`}
        confirmLabel="Converter"
        tone="primary"
        onConfirm={() => {
          convert.mutate();
          setConverting(false);
        }}
        onCancel={() => setConverting(false)}
      />
    </>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 col-lg-3 fw-normal text-muted">{label}</dt>
      <dd className="col-sm-8 col-lg-9">{children || "—"}</dd>
    </>
  );
}
